import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { deviceToday, eventName, eventPlace, sortEvents } from '@wr/shared';
import { useOffline } from '../offline/context.js';
import type { LocalRecord } from '../offline/store.js';
import { Dialog } from './ui.js';
import { DECLINED } from '../screens/capture/AskEvent.js';

/**
 * The current event of this device (brief section 5): every contact and
 * lead captured here is tagged with it. Shown by the chip of the top bar
 * and the side menu; both open the picker, a history step (Back closes it).
 */
export function useCurrentEvent() {
  const { store, counts, eventId, setEventId } = useOffline();
  const [events, setEvents] = useState<LocalRecord[]>([]);
  useEffect(() => {
    if (!store) return;
    let current = true;
    void store
      .listRecords('event')
      .then((list) => current && setEvents(sortEvents(list.filter((e) => !e.deleted))));
    return () => {
      current = false;
    };
    // counts: a sync ran (events received) or a change was saved.
  }, [store, counts, eventId]);
  const found = (eventId && events.find((e) => e.id === eventId)) || null;
  // A wrapped up event (Completed) is no longer anyone's current event: no more capture on it.
  const closed = found?.status === 'completed';
  useEffect(() => {
    if (closed) void setEventId(null);
  }, [closed, setEventId]);
  return { eventId: closed ? null : eventId, event: closed ? null : found, events };
}

/** Opens the picker over the current page, as a history step. */
export function usePicker() {
  const location = useLocation();
  const navigate = useNavigate();
  const open = (location.state as { picker?: boolean } | null)?.picker === true;
  return {
    open,
    show: (replace = false) =>
      navigate(location.pathname + location.search, {
        replace,
        state: { ...((location.state as object | null) ?? {}), menu: false, picker: true },
      }),
    close: () => navigate(-1),
  };
}

/** The chip of the top bar: the current event, or Pick event. */
export function EventChip() {
  const { event } = useCurrentEvent();
  const picker = usePicker();
  const name = event ? eventName(event) : null;
  return (
    <button
      type="button"
      className={`event-chip${name ? '' : ' none'}`}
      aria-label={name ? `Current event: ${name}. Change it` : undefined}
      onClick={() => picker.show()}
    >
      <i className="dot" />
      <span>{name ?? 'Pick event'}</span>
    </button>
  );
}

/** The side menu's line: the current event, or Office day, and Change. */
export function MenuEvent() {
  const { event } = useCurrentEvent();
  const picker = usePicker();
  return (
    <button type="button" className="dr-ev" onClick={() => picker.show(true)}>
      <span>
        <small>Current event</small>
        <b>{event ? eventName(event) : 'Office day'}</b>
      </span>
      <span className="linkbtn">Change</span>
    </button>
  );
}

/** The picker (prototype: Current event): no event, or one of the active events. */
export function EventPicker() {
  const { eventId, events } = useCurrentEvent();
  const { setEventId, store } = useOffline();
  const picker = usePicker();
  const navigate = useNavigate();
  if (!picker.open) return null;
  const pick = async (id: string | null) => {
    await setEventId(id);
    // No event chosen: capture does not ask again today (Nov 3, 2026).
    if (!id) await store?.setMeta(DECLINED, deviceToday()).catch(() => undefined);
    picker.close();
  };
  const active = events.filter((e) => e.status !== 'completed');
  return (
    <Dialog title="Current event" onClose={picker.close}>
      <p className="note">Every contact and lead you capture on this device is tagged with it.</p>
      <div className="evlist">
        <button type="button" aria-pressed={!eventId} onClick={() => void pick(null)}>
          No event (office, visit, call)
        </button>
        {active.map((e) => (
          <button
            key={e.id}
            type="button"
            aria-pressed={e.id === eventId}
            onClick={() => void pick(e.id)}
          >
            <span>
              <b className="evlist-name">{eventName(e)}</b>
              <br />
              <small className="note">{eventPlace(e)}</small>
            </span>
            {e.id === eventId && <span>Selected</span>}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="linkbtn"
        onClick={() => navigate('/events', { replace: true })}
      >
        Manage events
      </button>
    </Dialog>
  );
}
